import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type DnsPayload = {
  A?: string[];
  AAAA?: string[];
  NS?: string[];
  CNAME?: string[];
  PTR?: string[];
  NAPTR?: string[];
  TXT?: string[];
  MX?: Array<{ exchange: string; priority: number }>;
  SRV?: Array<Record<string, any>>;
  SOA?: Partial<{
    nsname: string;
    hostmaster: string;
    serial: number;
    refresh: number;
    retry: number;
    expire: number;
    minttl: number;
  }>;
};

function getPayload(results: any[]): DnsPayload | null {
  if (!Array.isArray(results) || results.length === 0) return null;
  const first = results[0];
  return first && typeof first === 'object' ? first as DnsPayload : null;
}

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
    <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-gradient-to-r from-sky-50 to-indigo-50 text-slate-700">
      {title}
    </div>
    <div className="p-3 text-sm">{children}</div>
  </div>
);

const ListTable: React.FC<{ items: string[] }> = ({ items }) => (
  items.length === 0 ? <div className="text-slate-500">—</div> : (
    <table className="w-full">
      <tbody className="divide-y divide-slate-200">
        {items.map((v, i) => (
          <tr key={i}><td className="py-1.5 text-slate-900 break-words">{v}</td></tr>
        ))}
      </tbody>
    </table>
  )
);

const DnsAllRecordsResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const data = getPayload(results);

  if (!data) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.dns_all_records.empty')}</p>;
  }

  const A     = data.A ?? [];
  const AAAA  = data.AAAA ?? [];
  const NS    = data.NS ?? [];
  const CNAME = data.CNAME ?? [];
  const PTR   = data.PTR ?? [];
  const NAPTR = data.NAPTR ?? [];
  const TXT   = data.TXT ?? [];
  const MX    = Array.isArray(data.MX) ? data.MX : [];
  const SRV   = Array.isArray(data.SRV) ? data.SRV : [];
  const SOA   = data.SOA ?? {};

  // ¿no hay nada?
  const hasAny =
    A.length || AAAA.length || NS.length || CNAME.length || PTR.length ||
    NAPTR.length || TXT.length || MX.length || SRV.length || Object.keys(SOA).length;

  if (!hasAny) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.dns_all_records.empty')}</p>;
  }

  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {/* A */}
      <SectionCard title="A">
        <ListTable items={A} />
      </SectionCard>

      {/* AAAA */}
      <SectionCard title="AAAA">
        <ListTable items={AAAA} />
      </SectionCard>

      {/* NS */}
      <SectionCard title="NS">
        <ListTable items={NS} />
      </SectionCard>

      {/* CNAME */}
      <SectionCard title="CNAME">
        <ListTable items={CNAME} />
      </SectionCard>

      {/* PTR */}
      <SectionCard title="PTR">
        <ListTable items={PTR} />
      </SectionCard>

      {/* NAPTR */}
      <SectionCard title="NAPTR">
        <ListTable items={NAPTR} />
      </SectionCard>

      {/* TXT */}
      <SectionCard title="TXT">
        <ListTable items={TXT} />
      </SectionCard>

      {/* MX */}
      <SectionCard title="MX">
        {MX.length === 0 ? (
          <div className="text-slate-500">—</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-slate-600">
                <th className="py-1.5 text-left text-[11px] uppercase tracking-wide">{t('tools.results_scan.dns_all_records.priority')}</th>
                <th className="py-1.5 text-left text-[11px] uppercase tracking-wide">{t('tools.results_scan.dns_all_records.exchange')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {MX.map((r, i) => (
                <tr key={i}>
                  <td className="py-1.5 pr-3 text-slate-900">{r.priority}</td>
                  <td className="py-1.5 text-slate-900 break-words">{r.exchange}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {/* SRV */}
      <SectionCard title="SRV">
        {SRV.length === 0 ? (
          <div className="text-slate-500">—</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-slate-600">
                {Object.keys(SRV[0]).map(k => (
                  <th key={k} className="py-1.5 text-left text-[11px] uppercase tracking-wide">{k}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {SRV.map((row, i) => (
                <tr key={i}>
                  {Object.keys(SRV[0]).map(k => (
                    <td key={k} className="py-1.5 text-slate-900 break-words">
                      {String(row?.[k] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {/* SOA */}
      <SectionCard title="SOA">
        {Object.keys(SOA).length === 0 ? (
          <div className="text-slate-500">—</div>
        ) : (
          <table className="w-full">
            <tbody className="divide-y divide-slate-200">
              {Object.entries(SOA).map(([k, v]) => (
                <tr key={k}>
                  <td className="py-1.5 pr-3 font-medium text-slate-700 w-36">{k}</td>
                  <td className="py-1.5 text-slate-900 break-words">{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>
    </div>
  );
};

export default DnsAllRecordsResults;
